import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Calendar, Users, CheckCircle, XCircle, LogOut, Lock, Download, Ban, Edit, AlertCircle, Bell, Trash2, Archive, Printer } from "lucide-react";
import { format, parse, addDays } from "date-fns";
import { de } from "date-fns/locale";
import ReactCalendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import { ReceiptPrintTemplate, A4PrintTemplate } from "../components/PrintTemplate";
import { ReservationGuestEditor } from "@/components/ReservationGuestEditor";
import { hasGuestBreakdown, GuestSummary } from "@/components/GuestSummary";
import { validGuestCounts } from "@/components/GuestCountFields";
import { reservationErrorMessage } from "@/lib/reservationErrors";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [reservations, setReservations] = useState([]);
  const [filteredReservations, setFilteredReservations] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("active");
  const [loading, setLoading] = useState(false);
  const [authToken, setAuthToken] = useState("");
  
  // Activity Feed State
  const [activities, setActivities] = useState([]);
  const [showActivityFeed, setShowActivityFeed] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [activityLimit, setActivityLimit] = useState(20);
  
  // Calendar State
  const [selectedCalendarDate, setSelectedCalendarDate] = useState(null);
  const [showDatePicker, setShowDatePicker] = useState(false);
  
  // Edit State
  const [editingReservation, setEditingReservation] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editGuestCounts, setEditGuestCounts] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [editTime, setEditTime] = useState("");
  const [editDate, setEditDate] = useState(new Date());
  const [editSpecialRequests, setEditSpecialRequests] = useState("");
  const [showEditCalendar, setShowEditCalendar] = useState(false);
  const [editFirstName, setEditFirstName] = useState("");
  const [editLastName, setEditLastName] = useState("");
  
  // Block Time State
  const [blockedSlots, setBlockedSlots] = useState([]);
  const [showBlockDialog, setShowBlockDialog] = useState(false);
  const [blockDate, setBlockDate] = useState(new Date());
  const [blockTimeFrom, setBlockTimeFrom] = useState("");
  const [blockTimeTo, setBlockTimeTo] = useState("");
  const [isFullDayBlock, setIsFullDayBlock] = useState(false);
  const [blockReason, setBlockReason] = useState("");
  const [showCalendar, setShowCalendar] = useState(false);
  
  // Settings State
  const [settings, setSettings] = useState(null);
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);
  const [contactName, setContactName] = useState("");
  const [lastBookingOffset, setLastBookingOffset] = useState(30);
  const [selectedDay, setSelectedDay] = useState("monday");
  const [openingHours, setOpeningHours] = useState({
    monday: { lunch_enabled: true, lunch_opening: "10:30", lunch_closing: "14:00", dinner_enabled: true, dinner_opening: "17:00", dinner_closing: "22:30" },
    tuesday: { lunch_enabled: true, lunch_opening: "10:30", lunch_closing: "14:00", dinner_enabled: true, dinner_opening: "17:00", dinner_closing: "22:30" },
    wednesday: { lunch_enabled: true, lunch_opening: "10:30", lunch_closing: "14:00", dinner_enabled: true, dinner_opening: "17:00", dinner_closing: "22:30" },
    thursday: { lunch_enabled: true, lunch_opening: "10:30", lunch_closing: "14:00", dinner_enabled: true, dinner_opening: "17:00", dinner_closing: "22:30" },
    friday: { lunch_enabled: true, lunch_opening: "10:30", lunch_closing: "14:00", dinner_enabled: true, dinner_opening: "17:00", dinner_closing: "22:30" },
    saturday: { lunch_enabled: false, lunch_opening: "10:30", lunch_closing: "14:00", dinner_enabled: true, dinner_opening: "17:00", dinner_closing: "22:30" },
    sunday: { lunch_enabled: false, lunch_opening: "10:30", lunch_closing: "14:00", dinner_enabled: true, dinner_opening: "17:00", dinner_closing: "22:30" }
  });
  
  // Confirm/Reject State
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectingReservation, setRejectingReservation] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [predefinedReasons] = useState([
    "Leider ausgebucht",
    "Zu große Gruppe für unsere Kapazität",
    "Personal-Engpass",
    "Kurzfristige Anfrage",
    "Betriebsferien/Geschlossen"
  ]);
  
  // Auto-Print State
  const [autoPrintEnabled, setAutoPrintEnabled] = useState(() => {
    return localStorage.getItem('autoPrintEnabled') === 'true';
  });
  const [printingReservation, setPrintingReservation] = useState(null);
  const [lastReservationCount, setLastReservationCount] = useState(0);
  const [printedReservationIds, setPrintedReservationIds] = useState(new Set());
  const [printerFormat, setPrinterFormat] = useState(() => {
    return localStorage.getItem('printerFormat') || 'receipt'; // 'receipt' or 'a4'
  });
  const [showPrinterSettings, setShowPrinterSettings] = useState(false);
  const [showArchiveMenu, setShowArchiveMenu] = useState(false);
  const [availablePrinters, setAvailablePrinters] = useState([]);
  const [selectedPrinter, setSelectedPrinter] = useState(() => {
    return localStorage.getItem('selectedPrinter') || '';
  });

  const fetchReservations = useCallback(async () => {
    try {
      let url = `${API}/reservations`;
      const params = new URLSearchParams();
      
      // Immer ALLE Reservationen laden (außer bei Kalender-Filter)
      if (selectedCalendarDate) {
        params.append('date', format(selectedCalendarDate, 'yyyy-MM-dd'));
      }
      
      // KEIN Status-Filter mehr - wir laden alle und filtern im Frontend
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      const response = await axios.get(url, {
        headers: { Authorization: `Basic ${authToken}` }
      });
      setReservations(response.data);
      // Filtering wird automatisch durch useEffect getriggert
    } catch (error) {
      console.error("Error fetching reservations:", error);
      if (error.response?.status === 401) {
        handleLogout();
      }
    }
  }, [authToken, selectedCalendarDate]);

  const fetchActivities = useCallback(async () => {
    if (!isAuthenticated) return;
    
    try {
      const response = await axios.get(`${API}/activities?limit=${activityLimit}`, {
        headers: { Authorization: `Basic ${authToken}` }
      });
      setActivities(response.data.activities || []);
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error("Error fetching activities:", error);
    }
  }, [isAuthenticated, authToken, activityLimit]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchReservations();
      fetchActivities();
      fetchBlockedSlots();
      fetchSettings();
      
      // Auto-refresh alle 5 Sekunden
      const interval = setInterval(() => {
        fetchActivities();
        fetchReservations();
      }, 5000);
      
      return () => clearInterval(interval);
    }
  }, [isAuthenticated, fetchReservations, fetchActivities]);
  
  // Auto-Print Effect - Check for new reservations and changes
  useEffect(() => {
    if (!autoPrintEnabled || !isAuthenticated || reservations.length === 0) return;
    
    // Initialize on first load
    if (printedReservationIds.size === 0) {
      const initialIds = new Set(reservations.map(r => r.id));
      setPrintedReservationIds(initialIds);
      setLastReservationCount(reservations.length);
      console.log("Auto-Print initialized with existing reservations");
      return;
    }
    
    // Find new reservations (nur wirklich neue, mit confirmed/pending Status)
    const reservationsToPrint = [];
    
    reservations.forEach(reservation => {
      // Nur neue Reservationen drucken (nicht completed oder rejected)
      if (!printedReservationIds.has(reservation.id) && 
          reservation.status !== 'completed' && 
          reservation.status !== 'rejected') {
        reservationsToPrint.push(reservation);
      }
    });
    
    // Print new reservations
    if (reservationsToPrint.length > 0) {
      reservationsToPrint.forEach((reservation, index) => {
        setTimeout(() => {
          printReservation(reservation);
          console.log(`Auto-Druck: ${reservation.first_name} ${reservation.last_name}`);
        }, index * 1000); // 1 second delay between prints
      });
      
      // Update printed IDs set
      const newPrintedIds = new Set([...printedReservationIds, ...reservationsToPrint.map(r => r.id)]);
      setPrintedReservationIds(newPrintedIds);
    }
    
    setLastReservationCount(reservations.length);
  }, [reservations, autoPrintEnabled, isAuthenticated]);

  useEffect(() => {
    filterReservations();
  }, [searchTerm, statusFilter, reservations]);

  const handleLogin = async () => {
    setLoading(true);
    const credentials = btoa(`admin:${password}`);
    
    try {
      const response = await axios.get(`${API}/reservations`, {
        headers: { Authorization: `Basic ${credentials}` }
      });
      
      setAuthToken(credentials);
      localStorage.setItem('adminToken', credentials);
      setIsAuthenticated(true);
      setReservations(response.data);
      toast.success("Erfolgreich angemeldet");
    } catch (error) {
      toast.error("Ungültiges Passwort");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setAuthToken("");
    setPassword("");
    localStorage.removeItem('adminToken');
    setReservations([]);
    toast.success("Erfolgreich abgemeldet");
  };

  const markActivitiesAsRead = async () => {
    try {
      await axios.post(`${API}/activities/mark-read`, null, {
        headers: { Authorization: `Basic ${authToken}` }
      });
      setUnreadCount(0);
      setActivities(activities.map(a => ({ ...a, is_read: true })));
    } catch (error) {
      console.error("Error marking activities as read:", error);
    }
  };

  const filterReservations = () => {
    let filtered = reservations;
    
    // Status-Filter anwenden
    if (statusFilter === 'completed') {
      filtered = filtered.filter(r => r.status === 'completed');
    } else if (statusFilter === 'deleted') {
      filtered = filtered.filter(r => r.status === 'deleted');
    } else if (statusFilter === 'rejected') {
      filtered = filtered.filter(r => r.status === 'rejected');
    } else if (statusFilter === 'active') {
      // Aktiv = confirmed + pending (nicht completed, deleted, rejected)
      filtered = filtered.filter(r => 
        r.status !== 'completed' && 
        r.status !== 'deleted' && 
        r.status !== 'rejected'
      );
    } else if (statusFilter !== 'all') {
      filtered = filtered.filter(r => r.status === statusFilter);
    }
    
    // Such-Filter anwenden
    if (searchTerm) {
      filtered = filtered.filter(res =>
        res.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        res.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        res.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    setFilteredReservations(filtered);
  };

  const fetchBlockedSlots = async () => {
    try {
      const response = await axios.get(`${API}/blocked-slots`);
      setBlockedSlots(response.data);
    } catch (error) {
      console.error("Error fetching blocked slots:", error);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSettings(response.data);
      setContactName(response.data.contact_name);
      setLastBookingOffset(response.data.last_booking_offset_minutes || 30);
      if (response.data.opening_hours) {
        setOpeningHours(response.data.opening_hours);
      }
    } catch (error) {
      console.error("Error fetching settings:", error);
    }
  };

  const handleDeleteReservation = async (reservationId) => {
    if (!window.confirm("Möchten Sie diese Reservierung wirklich löschen?")) {
      return;
    }
    
    try {
      const response = await axios.delete(`${API}/reservations/${reservationId}`, {
        headers: { Authorization: `Basic ${authToken}` }
      });
      
      console.log("Delete response:", response.data);
      
      // Lokale Aktualisierung - Entferne aus der Liste
      setReservations(prevReservations => 
        prevReservations.filter(r => r.id !== reservationId)
      );
      
      toast.success("Reservierung erfolgreich gelöscht");
      fetchActivities();
    } catch (error) {
      console.error("Error deleting reservation:", error);
      console.error("Error details:", error.response?.data);
      toast.error(`Fehler beim Löschen: ${error.response?.data?.detail || error.message}`);
    }
  };

  const handleMarkAsCompleted = async (reservationId) => {
    try {
      await axios.put(
        `${API}/reservations/${reservationId}/admin`,
        { status: "completed" },
        { headers: { Authorization: `Basic ${authToken}` } }
      );
      
      // Lokale Aktualisierung - Update in der aktuellen Liste
      setReservations(prevReservations => 
        prevReservations.map(r => 
          r.id === reservationId ? { ...r, status: 'completed' } : r
        )
      );
      
      toast.success("Reservierung als erledigt markiert", {
        description: "Klicken Sie auf 'Erledigte' Filter um sie anzuzeigen",
        duration: 5000
      });
      
      fetchActivities();
    } catch (error) {
      toast.error("Fehler beim Aktualisieren");
      console.error("Error updating reservation:", error);
    }
  };

  const handleStatusChange = async (reservationId, newStatus) => {
    try {
      await axios.put(
        `${API}/reservations/${reservationId}/admin`,
        { status: newStatus },
        { headers: { Authorization: `Basic ${authToken}` } }
      );
      
      // Lokale Aktualisierung
      setReservations(prevReservations => 
        prevReservations.map(r => 
          r.id === reservationId ? { ...r, status: newStatus } : r
        )
      );
      
      const statusLabels = {
        pending: 'Anfrage',
        confirmed: 'Bestätigt',
        completed: 'Erledigt',
        rejected: 'Abgelehnt'
      };
      
      toast.success(`Status geändert zu: ${statusLabels[newStatus]}`);
      fetchActivities();
    } catch (error) {
      toast.error("Fehler beim Aktualisieren");
      console.error("Error updating reservation:", error);
    }
  };

  const handleConfirmReservation = async (reservationId) => {
    try {
      await axios.post(
        `${API}/reservations/${reservationId}/confirm`,
        {},
        { headers: { Authorization: `Basic ${authToken}` } }
      );
      toast.success("Anfrage erfolgreich bestätigt! E-Mail wurde an Kunde gesendet.");
      fetchReservations();
      fetchActivities();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Bestätigen");
      console.error("Error confirming reservation:", error);
    }
  };

  const openRejectDialog = (reservation) => {
    setRejectingReservation(reservation);
    setRejectionReason("");
    setShowRejectDialog(true);
  };

  const handleRejectReservation = async () => {
    if (!rejectionReason.trim()) {
      toast.error("Bitte geben Sie eine Begründung an");
      return;
    }

    try {
      await axios.post(
        `${API}/reservations/${rejectingReservation.id}/reject`,
        { reason: rejectionReason },
        { headers: { Authorization: `Basic ${authToken}` } }
      );
      toast.success("Anfrage abgelehnt! E-Mail mit Begründung wurde an Kunde gesendet.");
      setShowRejectDialog(false);
      setRejectingReservation(null);
      setRejectionReason("");
      fetchReservations();
      fetchActivities();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Ablehnen");
      console.error("Error rejecting reservation:", error);
    }
  };

  const toggleAutoPrint = () => {
    const newValue = !autoPrintEnabled;
    setAutoPrintEnabled(newValue);
    localStorage.setItem('autoPrintEnabled', newValue.toString());
    
    if (newValue) {
      toast.success("✅ Auto-Druck aktiviert - Neue Reservationen werden automatisch gedruckt", { duration: 5000 });
      // Initialize printed IDs with current reservations
      const currentIds = new Set(reservations.map(r => r.id));
      setPrintedReservationIds(currentIds);
    } else {
      toast.info("❌ Auto-Druck deaktiviert", { duration: 3000 });
    }
  };

  const printReservation = (reservation) => {
    setPrintingReservation(reservation);
    
    // Use a timeout to ensure state updates before print
    setTimeout(() => {
      // Trigger browser print dialog
      window.print();
      
      // Info message
      if (autoPrintEnabled) {
        console.log(`Auto-Druck: ${reservation.first_name} ${reservation.last_name}`);
      } else {
        toast.info(`Druckdialog geöffnet für ${reservation.first_name} ${reservation.last_name}`);
      }
    }, 100);
  };

  const fetchPrinters = async () => {
    try {
      const response = await axios.get(`${API}/printers`, {
        headers: { Authorization: `Basic ${authToken}` }
      });
      setAvailablePrinters(response.data.printers || []);
    } catch (error) {
      console.error("Error fetching printers:", error);
      toast.error("Fehler beim Laden der Drucker");
    }
  };

  const handlePrinterFormatChange = (format) => {
    setPrinterFormat(format);
    localStorage.setItem('printerFormat', format);
    toast.success(format === 'receipt' ? "Belegdrucker-Format aktiviert" : "A4-Format aktiviert");
  };
  
  const handlePrinterChange = (printerName) => {
    setSelectedPrinter(printerName);
    localStorage.setItem('selectedPrinter', printerName);
    toast.success(`Drucker gewählt: ${printerName}`);
  };

  const testPrint = () => {
    const testReservation = {
      id: "TEST-123",
      first_name: "Test",
      last_name: "Kunde",
      email: "test@example.com",
      phone: "+41 79 123 4567",
      reservation_date: format(new Date(), "yyyy-MM-dd"),
      reservation_time: "19:00",
      guests_count: 4,
      adults_count: 2,
      children_count: 2,
      special_requests: "Testdruck - Bitte Format prüfen",
      status: "confirmed"
    };
    printReservation(testReservation);
  };

  const openEditDialog = (reservation) => {
    setEditingReservation(reservation);
    setEditGuestCounts(hasGuestBreakdown(reservation) ? {
      adults_count: reservation.adults_count, children_count: reservation.children_count
    } : null);
    setEditTime(reservation.reservation_time);
    setEditDate(parse(reservation.reservation_date, "yyyy-MM-dd", new Date()));
    setEditSpecialRequests(reservation.special_requests || "");
    setEditFirstName(reservation.first_name);
    setEditLastName(reservation.last_name);
    setShowEditDialog(true);
  };

  const handleSaveEdit = async () => {
    if (savingEdit) return;
    if (editGuestCounts && !validGuestCounts(editGuestCounts)) {
      toast.error("Bitte wählen Sie mindestens eine erwachsene Person.");
      return;
    }
    setSavingEdit(true);
    try {
      await axios.put(
        `${API}/reservations/${editingReservation.id}/admin`,
        {
          ...(editGuestCounts || {}),
          reservation_time: editTime,
          reservation_date: format(editDate, "yyyy-MM-dd"),
          special_requests: editSpecialRequests,
          first_name: editFirstName,
          last_name: editLastName
        },
        { headers: { Authorization: `Basic ${authToken}` } }
      );
      toast.success("Reservierung erfolgreich aktualisiert");
      setShowEditDialog(false);
      fetchReservations();
      fetchActivities();
    } catch (error) {
      toast.error(reservationErrorMessage(error, "Fehler beim Aktualisieren"));
      console.error("Error updating reservation:", error);
    } finally {
      setSavingEdit(false);
    }
  };

  const handleBlockTimeSlot = async () => {
    try {
      const blockData = {
        date: format(blockDate, "yyyy-MM-dd"),
        is_full_day: isFullDayBlock,
        reason: blockReason
      };
      
      if (!isFullDayBlock) {
        blockData.time_from = blockTimeFrom;
        blockData.time_to = blockTimeTo;
      }
      
      await axios.post(`${API}/blocked-slots`, blockData, {
        headers: { Authorization: `Basic ${authToken}` }
      });
      
      toast.success("Zeitslot erfolgreich gesperrt");
      setShowBlockDialog(false);
      fetchBlockedSlots();
      setBlockTimeFrom("");
      setBlockTimeTo("");
      setBlockReason("");
      setIsFullDayBlock(false);
    } catch (error) {
      toast.error("Fehler beim Sperren des Zeitslots");
      console.error("Error blocking time slot:", error);
    }
  };

  const handleUnblockTimeSlot = async (slotId) => {
    try {
      await axios.delete(`${API}/blocked-slots/${slotId}`, {
        headers: { Authorization: `Basic ${authToken}` }
      });
      toast.success("Zeitslot entsperrt");
      fetchBlockedSlots();
    } catch (error) {
      toast.error("Fehler beim Entsperren");
      console.error("Error unblocking slot:", error);
    }
  };

  const handleSaveSettings = async () => {
    try {
      await axios.put(
        `${API}/settings`,
        {
          contact_name: contactName,
          last_booking_offset_minutes: lastBookingOffset,
          opening_hours: openingHours
        },
        { headers: { Authorization: `Basic ${authToken}` } }
      );
      toast.success("Einstellungen gespeichert");
      setShowSettingsDialog(false);
      fetchSettings();
    } catch (error) {
      toast.error("Fehler beim Speichern");
      console.error("Error saving settings:", error);
    }
  };

  const handleExportExcel = async () => {
    try {
      const response = await axios.get(`${API}/reservations/export/excel`, {
        headers: { Authorization: `Basic ${authToken}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `reservierungen_${format(new Date(), 'yyyyMMdd')}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Excel-Datei heruntergeladen");
    } catch (error) {
      toast.error("Fehler beim Export");
      console.error("Error exporting excel:", error);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { bg: "bg-yellow-100", text: "text-yellow-800", icon: AlertCircle, label: "ANFRAGE" },
      confirmed: { bg: "bg-green-100", text: "text-green-800", icon: CheckCircle, label: "BESTÄTIGT" },
      cancelled: { bg: "bg-red-100", text: "text-red-800", icon: XCircle, label: "STORNIERT" },
      rejected: { bg: "bg-orange-100", text: "text-orange-800", icon: XCircle, label: "ABGELEHNT" },
      completed: { bg: "bg-gray-100", text: "text-gray-800", icon: Archive, label: "ERLEDIGT" },
      deleted: { bg: "bg-red-200", text: "text-red-900", icon: Trash2, label: "GELÖSCHT" }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-semibold ${config.bg} ${config.text}`}>
        <Icon className="w-4 h-4" />
        {config.label}
      </span>
    );
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case 'new_reservation':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'cancelled':
        return <XCircle className="w-5 h-5 text-red-600" />;
      case 'updated':
        return <Edit className="w-5 h-5 text-blue-600" />;
      case 'large_group_inquiry':
        return <AlertCircle className="w-5 h-5 text-orange-600" />;
      case 'deleted':
        return <Trash2 className="w-5 h-5 text-gray-600" />;
      default:
        return <Bell className="w-5 h-5 text-gray-600" />;
    }
  };

  const getActivityColor = (type) => {
    switch (type) {
      case 'new_reservation':
        return 'border-l-green-500';
      case 'cancelled':
        return 'border-l-red-500';
      case 'updated':
        return 'border-l-blue-500';
      case 'large_group_inquiry':
        return 'border-l-orange-500';
      case 'deleted':
        return 'border-l-gray-500';
      default:
        return 'border-l-gray-300';
    }
  };

  const formatActivityTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMinutes = Math.floor((now - date) / 60000);
    
    if (diffMinutes < 1) return 'Gerade eben';
    if (diffMinutes < 60) return `vor ${diffMinutes} Min`;
    
    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24) return `vor ${diffHours} Std`;
    
    const diffDays = Math.floor(diffHours / 24);
    return `vor ${diffDays} Tag${diffDays > 1 ? 'en' : ''}`;
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{backgroundColor: '#f8f6f3'}}>
        <div className="bg-white p-8 rounded-2xl shadow-xl max-w-md w-full">
          <div className="text-center mb-6">
            <Lock className="w-16 h-16 mx-auto mb-4" style={{color: '#c8232c'}} />
            <h1 className="text-3xl font-bold mb-2" style={{color: '#2c2c2c'}}>Admin Login</h1>
            <p className="text-gray-600">Pizzeria Piazza Reservierungssystem</p>
          </div>
          <div className="space-y-4">
            <div>
              <Label>Passwort</Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
                placeholder="Admin-Passwort eingeben"
              />
            </div>
            <Button
              onClick={handleLogin}
              disabled={loading}
              className="w-full text-white"
              style={{backgroundColor: '#c8232c'}}
            >
              {loading ? "Anmeldung..." : "Anmelden"}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const stats = {
    total: reservations.length,
    pending: reservations.filter(r => r.status === "pending").length,
    confirmed: reservations.filter(r => r.status === "confirmed").length,
    cancelled: reservations.filter(r => r.status === "cancelled").length,
    completed: reservations.filter(r => r.status === "completed").length
  };

  return (
    <div className="min-h-screen p-3 md:p-6" style={{backgroundColor: '#f8f6f3'}}>
      <div className="max-w-7xl mx-auto">
        {/* Header with Activity Bell */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 md:mb-8 gap-4">
          <div>
            <h1 className="text-2xl md:text-4xl font-bold" style={{color: '#2c2c2c'}}>Admin Dashboard</h1>
            <p className="text-xs md:text-base text-gray-600 mt-1 md:mt-2">Pizzeria Piazza Reservierungsverwaltung</p>
          </div>
          <div className="flex gap-2 md:gap-4 items-center flex-wrap">
            {/* Archiv Dropdown */}
            <Dialog open={showArchiveMenu} onOpenChange={setShowArchiveMenu}>
              <DialogTrigger asChild>
                <Button
                  variant="outline"
                  className="text-xs md:text-sm h-9 md:h-10"
                >
                  📦 <span className="hidden md:inline">Archiv</span>
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-xs">
                <DialogHeader>
                  <DialogTitle>Archiv</DialogTitle>
                </DialogHeader>
                <div className="space-y-2">
                  <Button
                    onClick={() => {
                      setStatusFilter("completed");
                      setShowArchiveMenu(false);
                    }}
                    variant="outline"
                    className="w-full justify-start"
                  >
                    <Archive className="w-4 h-4 mr-2" />
                    Erledigte Reservationen ({stats.completed})
                  </Button>
                  <Button
                    onClick={() => {
                      setStatusFilter("deleted");
                      setShowArchiveMenu(false);
                    }}
                    variant="outline"
                    className="w-full justify-start text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Gelöschte Reservationen ({reservations.filter(r => r.status === 'deleted').length})
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
            
            {/* Refresh Button */}
            <Button
              onClick={() => {
                fetchReservations();
                fetchActivities();
                toast.success("Aktualisiert!");
              }}
              variant="outline"
              title="Aktualisieren"
              className="text-xs md:text-sm h-9 md:h-10"
            >
              🔄 <span className="hidden md:inline">Aktualisieren</span>
            </Button>
            
            {/* Printer Settings Dialog */}
            <Dialog open={showPrinterSettings} onOpenChange={setShowPrinterSettings}>
              <DialogTrigger asChild>
                <Button variant="outline" title="Drucker-Einstellungen" className="text-xs md:text-sm h-9 md:h-10">
                  ⚙️ <span className="hidden md:inline">Drucker</span>
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>🖨️ Drucker-Einstellungen</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                    <p className="text-sm text-blue-800 mb-2">
                      <strong>📋 So richten Sie den Druck ein:</strong>
                    </p>
                    <ol className="text-xs text-blue-700 space-y-1 ml-4 list-decimal">
                      <li>Wählen Sie unten das gewünschte Papierformat</li>
                      <li>Klicken Sie auf "Testdruck durchführen"</li>
                      <li>Im Browser-Druckdialog: Wählen Sie Ihren <strong>Epson T20III</strong></li>
                      <li>Aktivieren Sie "Einstellungen merken" (falls verfügbar)</li>
                      <li>Aktivieren Sie oben den <strong>Auto-Druck</strong> Button</li>
                    </ol>
                    <p className="text-xs text-blue-700 mt-2 italic">
                      💡 Tipp: Chrome merkt sich Ihre Drucker-Auswahl für diese Seite
                    </p>
                  </div>
                  
                  <div>
                    <Label className="mb-3 block font-semibold">Papierformat wählen:</Label>
                    <div className="space-y-2">
                      <button
                        onClick={() => handlePrinterFormatChange('receipt')}
                        className={`w-full p-4 text-left border-2 rounded-lg transition ${
                          printerFormat === 'receipt'
                            ? 'border-green-500 bg-green-50'
                            : 'border-gray-300 hover:border-green-300'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="text-2xl">🧾</div>
                          <div>
                            <p className="font-semibold">Belegdrucker (58mm/80mm)</p>
                            <p className="text-sm text-gray-600">Für Thermodrucker / Kassendrucker</p>
                            {printerFormat === 'receipt' && (
                              <p className="text-xs text-green-600 mt-1">✓ Aktiv</p>
                            )}
                          </div>
                        </div>
                      </button>
                      
                      <button
                        onClick={() => handlePrinterFormatChange('a4')}
                        className={`w-full p-4 text-left border-2 rounded-lg transition ${
                          printerFormat === 'a4'
                            ? 'border-green-500 bg-green-50'
                            : 'border-gray-300 hover:border-green-300'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="text-2xl">📄</div>
                          <div>
                            <p className="font-semibold">A4 Drucker</p>
                            <p className="text-sm text-gray-600">Normaler Bürodrucker</p>
                            {printerFormat === 'a4' && (
                              <p className="text-xs text-green-600 mt-1">✓ Aktiv</p>
                            )}
                          </div>
                        </div>
                      </button>
                    </div>
                  </div>
                  
                  <div className="pt-4 border-t">
                    <Button
                      onClick={testPrint}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                    >
                      🧪 Testdruck durchführen
                    </Button>
                    <p className="text-xs text-gray-500 mt-2 text-center">
                      Druckt eine Test-Reservierung zum Überprüfen des Formats
                    </p>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
            
            {/* Auto-Print Toggle */}
            <Button
              onClick={toggleAutoPrint}
              variant={autoPrintEnabled ? "default" : "outline"}
              className={`text-xs md:text-sm h-9 md:h-10 ${autoPrintEnabled ? "bg-green-600 hover:bg-green-700 text-white" : ""}`}
              title={autoPrintEnabled ? "Auto-Druck aktiv" : "Auto-Druck inaktiv"}
            >
              🖨️ <span className="hidden sm:inline">{autoPrintEnabled ? "Auto-Druck AN" : "Auto-Druck AUS"}</span>
            </Button>
            
            {/* Activity Feed Bell */}
            <div className="relative">
              <Button
                onClick={() => {
                  setShowActivityFeed(!showActivityFeed);
                  if (!showActivityFeed && unreadCount > 0) {
                    markActivitiesAsRead();
                  }
                }}
                variant="outline"
                className="relative h-9 md:h-10 w-9 md:w-10 p-0"
              >
                <Bell className="w-4 h-4 md:w-5 md:h-5" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 md:-top-2 md:-right-2 bg-red-500 text-white text-[10px] md:text-xs font-bold rounded-full h-5 w-5 md:h-6 md:w-6 flex items-center justify-center">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </Button>
              
              {/* Activity Feed Dropdown */}
              {showActivityFeed && (
                <div className="absolute right-0 mt-2 w-[90vw] md:w-96 bg-white rounded-lg shadow-2xl border border-gray-200 z-50 max-h-[400px] md:max-h-[600px] overflow-y-auto">
                  <div className="p-4 border-b border-gray-200 sticky top-0 bg-white">
                    <h3 className="text-lg font-bold">Aktivitäten</h3>
                    <p className="text-sm text-gray-500">Letzte Änderungen</p>
                  </div>
                  <div className="divide-y divide-gray-100">
                    {activities.length === 0 ? (
                      <div className="p-6 text-center text-gray-500">
                        Keine Aktivitäten vorhanden
                      </div>
                    ) : (
                      activities.map((activity) => (
                        <div
                          key={activity.id}
                          className={`p-4 hover:bg-gray-100 transition border-l-4 cursor-pointer ${getActivityColor(activity.type)} ${!activity.is_read ? 'bg-blue-50' : ''}`}
                          onClick={() => {
                            // Schließe Activity Feed
                            setShowActivityFeed(false);
                            
                            // Finde die Reservierung
                            const reservation = reservations.find(r => r.id === activity.reservation_id);
                            if (reservation) {
                              // Öffne Edit Dialog
                              openEditDialog(reservation);
                              
                              // Alternativ: Scroll zur Reservierung in der Liste
                              setTimeout(() => {
                                const element = document.getElementById(`reservation-${activity.reservation_id}`);
                                if (element) {
                                  element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                  element.classList.add('highlight-flash');
                                  setTimeout(() => element.classList.remove('highlight-flash'), 2000);
                                }
                              }, 100);
                            }
                          }}
                        >
                          <div className="flex items-start gap-3">
                            <div className="mt-1">
                              {getActivityIcon(activity.type)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold text-gray-900 truncate">
                                {activity.customer_name}
                              </p>
                              <p className="text-sm text-gray-600 mt-1">
                                {activity.message}
                              </p>
                              <p className="text-xs text-gray-400 mt-1">
                                {formatActivityTime(activity.timestamp)}
                              </p>
                            </div>
                            <div className="text-gray-400">
                              →
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                  {activities.length >= activityLimit && (
                    <div className="p-4 border-t border-gray-200">
                      <Button
                        onClick={() => setActivityLimit(activityLimit + 20)}
                        variant="outline"
                        className="w-full"
                      >
                        Mehr anzeigen
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
            <Button onClick={handleLogout} variant="outline" className="text-xs md:text-sm h-9 md:h-10">
              <LogOut className="w-3 h-3 md:w-5 md:h-5 mr-1 md:mr-2" />
              <span className="hidden sm:inline">Abmelden</span>
            </Button>
          </div>
        </div>

        {/* Calendar Widget */}
        <div className="bg-white rounded-xl md:rounded-2xl shadow-lg p-3 md:p-6 mb-4 md:mb-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-3 md:mb-4 gap-2">
            <h2 className="text-base md:text-xl font-bold" style={{color: '#2c2c2c'}}>
              <Calendar className="inline-block w-4 h-4 md:w-6 md:h-6 mr-2" />
              Kalenderansicht
            </h2>
            {selectedCalendarDate && (
              <Button
                onClick={() => setSelectedCalendarDate(null)}
                variant="outline"
                size="sm"
              >
                Alle Tage anzeigen
              </Button>
            )}
          </div>
          <div className="flex justify-center">
            <ReactCalendar
              onChange={setSelectedCalendarDate}
              value={selectedCalendarDate}
              locale="de-DE"
              className="border-none shadow-sm"
            />
          </div>
          {selectedCalendarDate && (
            <div className="mt-4 p-4 bg-blue-50 rounded-lg">
              <p className="text-sm font-semibold text-blue-900">
                Anzeige für: {format(selectedCalendarDate, 'dd. MMMM yyyy', { locale: de })}
              </p>
              <p className="text-sm text-blue-700 mt-1">
                {filteredReservations.length} Reservierung{filteredReservations.length !== 1 ? 'en' : ''} gefunden
              </p>
            </div>
          )}
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
          <div className="bg-white p-6 rounded-2xl shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Total</p>
                <p className="text-3xl font-bold" style={{color: '#2c2c2c'}}>{stats.total}</p>
              </div>
              <Users className="w-10 h-10" style={{color: '#c8232c'}} />
            </div>
          </div>
          <div className="bg-white p-6 rounded-2xl shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Anfragen</p>
                <p className="text-3xl font-bold text-yellow-600">{stats.pending}</p>
              </div>
              <AlertCircle className="w-10 h-10 text-yellow-600" />
            </div>
          </div>
          <div className="bg-white p-6 rounded-2xl shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Bestätigt</p>
                <p className="text-3xl font-bold text-green-600">{stats.confirmed}</p>
              </div>
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
          </div>
          <div className="bg-white p-6 rounded-2xl shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Storniert</p>
                <p className="text-3xl font-bold text-red-600">{stats.cancelled}</p>
              </div>
              <XCircle className="w-10 h-10 text-red-600" />
            </div>
          </div>
          <div className="bg-white p-6 rounded-2xl shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Erledigt</p>
                <p className="text-3xl font-bold text-gray-600">{stats.completed}</p>
              </div>
              <Archive className="w-10 h-10 text-gray-600" />
            </div>
          </div>
        </div>

        {/* Actions Bar */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <div className="flex flex-wrap gap-4">
            <Input
              placeholder="Suche nach Name oder E-Mail..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-1 min-w-[200px]"
            />
            <Button onClick={handleExportExcel} variant="outline">
              <Download className="w-5 h-5 mr-2" />
              Excel Export
            </Button>
            <Dialog open={showBlockDialog} onOpenChange={setShowBlockDialog}>
              <DialogTrigger asChild>
                <Button variant="outline">
                  <Ban className="w-5 h-5 mr-2" />
                  Zeit-Slots sperren
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Zeit-Slot sperren</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label>Datum</Label>
                    <Button
                      onClick={() => setShowCalendar(!showCalendar)}
                      variant="outline"
                      className="w-full justify-start"
                    >
                      <Calendar className="w-4 h-4 mr-2" />
                      {format(blockDate, "dd.MM.yyyy")}
                    </Button>
                    {showCalendar && (
                      <div className="mt-2">
                        <CalendarComponent
                          mode="single"
                          selected={blockDate}
                          onSelect={(date) => {
                            setBlockDate(date);
                            setShowCalendar(false);
                          }}
                          locale={de}
                        />
                      </div>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="fullDayBlock"
                      checked={isFullDayBlock}
                      onChange={(e) => setIsFullDayBlock(e.target.checked)}
                    />
                    <Label htmlFor="fullDayBlock">Ganzer Tag sperren</Label>
                  </div>
                  
                  {!isFullDayBlock && (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Von (Zeit)</Label>
                        <Input
                          type="time"
                          value={blockTimeFrom}
                          onChange={(e) => setBlockTimeFrom(e.target.value)}
                        />
                      </div>
                      <div>
                        <Label>Bis (Zeit)</Label>
                        <Input
                          type="time"
                          value={blockTimeTo}
                          onChange={(e) => setBlockTimeTo(e.target.value)}
                        />
                      </div>
                    </div>
                  )}
                  
                  <div>
                    <Label>Grund (optional)</Label>
                    <Input
                      value={blockReason}
                      onChange={(e) => setBlockReason(e.target.value)}
                      placeholder="z.B. Betriebsferien, Privatveranstaltung"
                    />
                  </div>
                  
                  <Button onClick={handleBlockTimeSlot} className="w-full" style={{backgroundColor: '#c8232c'}}>
                    Zeit-Slot sperren
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
            
            <Button 
              onClick={() => navigate("/customers")} 
              variant="outline"
            >
              <Users className="w-5 h-5 mr-2" />
              Kunden
            </Button>
            
            <Dialog open={showSettingsDialog} onOpenChange={setShowSettingsDialog}>
              <DialogTrigger asChild>
                <Button variant="outline">Einstellungen</Button>
              </DialogTrigger>
              <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>⚙️ Einstellungen</DialogTitle>
                </DialogHeader>
                <div className="space-y-6">
                  {/* Kundenservice Name */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-semibold mb-3">Kundenservice</h3>
                    <div>
                      <Label>Kundenservice Name</Label>
                      <Input
                        value={contactName}
                        onChange={(e) => setContactName(e.target.value)}
                        placeholder="z.B. Pizzeria Piazza Team"
                      />
                    </div>
                  </div>

                  {/* Öffnungszeiten pro Wochentag */}
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h3 className="font-semibold mb-4">📅 Öffnungszeiten pro Wochentag</h3>
                    
                    {/* Tabs für Wochentage */}
                    <div className="flex flex-wrap gap-2 mb-4">
                      {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map(day => {
                        const dayNames = {
                          monday: 'Mo',
                          tuesday: 'Di',
                          wednesday: 'Mi',
                          thursday: 'Do',
                          friday: 'Fr',
                          saturday: 'Sa',
                          sunday: 'So'
                        };
                        return (
                          <button
                            key={day}
                            onClick={() => setSelectedDay(day)}
                            className={`px-4 py-2 rounded-lg font-semibold transition ${
                              selectedDay === day
                                ? 'bg-blue-600 text-white'
                                : 'bg-white text-gray-700 hover:bg-blue-100'
                            }`}
                          >
                            {dayNames[day]}
                          </button>
                        );
                      })}
                    </div>

                    {/* Öffnungszeiten für gewählten Tag */}
                    <div className="bg-white p-4 rounded-lg space-y-4">
                      <h4 className="font-semibold text-lg">
                        {(() => {
                          const fullNames = {
                            monday: 'Montag',
                            tuesday: 'Dienstag',
                            wednesday: 'Mittwoch',
                            thursday: 'Donnerstag',
                            friday: 'Freitag',
                            saturday: 'Samstag',
                            sunday: 'Sonntag'
                          };
                          return fullNames[selectedDay];
                        })()}
                      </h4>
                      
                      {/* Mittag */}
                      <div className="border-l-4 border-yellow-500 pl-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-semibold">☀️ Mittag</span>
                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={openingHours[selectedDay]?.lunch_enabled ?? true}
                              onChange={(e) => {
                                setOpeningHours(prev => ({
                                  ...prev,
                                  [selectedDay]: {
                                    ...prev[selectedDay],
                                    lunch_enabled: e.target.checked
                                  }
                                }));
                              }}
                              className="w-4 h-4"
                            />
                            <Label className="cursor-pointer">Geöffnet</Label>
                          </div>
                        </div>
                        {openingHours[selectedDay]?.lunch_enabled && (
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <Label className="text-sm">Von</Label>
                              <Input
                                type="time"
                                value={openingHours[selectedDay]?.lunch_opening || "10:30"}
                                onChange={(e) => {
                                  setOpeningHours(prev => ({
                                    ...prev,
                                    [selectedDay]: {
                                      ...prev[selectedDay],
                                      lunch_opening: e.target.value
                                    }
                                  }));
                                }}
                              />
                            </div>
                            <div>
                              <Label className="text-sm">Bis</Label>
                              <Input
                                type="time"
                                value={openingHours[selectedDay]?.lunch_closing || "14:00"}
                                onChange={(e) => {
                                  setOpeningHours(prev => ({
                                    ...prev,
                                    [selectedDay]: {
                                      ...prev[selectedDay],
                                      lunch_closing: e.target.value
                                    }
                                  }));
                                }}
                              />
                            </div>
                          </div>
                        )}
                        {!openingHours[selectedDay]?.lunch_enabled && (
                          <p className="text-sm text-gray-500 italic">Mittags geschlossen</p>
                        )}
                      </div>

                      {/* Abend */}
                      <div className="border-l-4 border-indigo-500 pl-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-semibold">🌙 Abend</span>
                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={openingHours[selectedDay]?.dinner_enabled ?? true}
                              onChange={(e) => {
                                setOpeningHours(prev => ({
                                  ...prev,
                                  [selectedDay]: {
                                    ...prev[selectedDay],
                                    dinner_enabled: e.target.checked
                                  }
                                }));
                              }}
                              className="w-4 h-4"
                            />
                            <Label className="cursor-pointer">Geöffnet</Label>
                          </div>
                        </div>
                        {openingHours[selectedDay]?.dinner_enabled && (
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <Label className="text-sm">Von</Label>
                              <Input
                                type="time"
                                value={openingHours[selectedDay]?.dinner_opening || "17:00"}
                                onChange={(e) => {
                                  setOpeningHours(prev => ({
                                    ...prev,
                                    [selectedDay]: {
                                      ...prev[selectedDay],
                                      dinner_opening: e.target.value
                                    }
                                  }));
                                }}
                              />
                            </div>
                            <div>
                              <Label className="text-sm">Bis</Label>
                              <Input
                                type="time"
                                value={openingHours[selectedDay]?.dinner_closing || "22:30"}
                                onChange={(e) => {
                                  setOpeningHours(prev => ({
                                    ...prev,
                                    [selectedDay]: {
                                      ...prev[selectedDay],
                                      dinner_closing: e.target.value
                                    }
                                  }));
                                }}
                              />
                            </div>
                          </div>
                        )}
                        {!openingHours[selectedDay]?.dinner_enabled && (
                          <p className="text-sm text-gray-500 italic">Abends geschlossen</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Reservierungs-Offset */}
                  <div className="bg-green-50 p-4 rounded-lg">
                    <h3 className="font-semibold mb-3">⏰ Letzte Reservierungszeit</h3>
                    <div>
                      <Label>Minuten vor Schließung</Label>
                      <Input
                        type="number"
                        min="0"
                        max="120"
                        value={lastBookingOffset}
                        onChange={(e) => setLastBookingOffset(parseInt(e.target.value) || 0)}
                        className="w-32"
                      />
                      <p className="text-sm text-gray-600 mt-2">
                        Kunden können bis <strong>{lastBookingOffset} Minuten</strong> vor Schließung reservieren
                      </p>
                    </div>
                  </div>

                  <Button onClick={handleSaveSettings} className="w-full" style={{backgroundColor: '#c8232c'}}>
                    Einstellungen Speichern
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="bg-white rounded-2xl shadow-lg mb-6">
          <div className="flex border-b">
            <button
              onClick={() => setStatusFilter("active")}
              className={`flex-1 py-4 px-4 md:px-6 font-semibold transition text-sm md:text-base ${
                statusFilter === "active"
                  ? "border-b-2 border-blue-600 text-blue-600"
                  : "text-gray-600 hover:text-blue-600"
              }`}
            >
              Aktiv ({reservations.filter(r => r.status !== 'completed' && r.status !== 'deleted' && r.status !== 'rejected').length})
            </button>
            <button
              onClick={() => setStatusFilter("pending")}
              className={`flex-1 py-4 px-4 md:px-6 font-semibold transition text-sm md:text-base ${
                statusFilter === "pending"
                  ? "border-b-2 border-yellow-600 text-yellow-600"
                  : "text-gray-600 hover:text-yellow-600"
              }`}
            >
              ⚠️ Anfragen ({stats.pending})
            </button>
            <button
              onClick={() => setStatusFilter("confirmed")}
              className={`flex-1 py-4 px-4 md:px-6 font-semibold transition text-sm md:text-base ${
                statusFilter === "confirmed"
                  ? "border-b-2 border-green-600 text-green-600"
                  : "text-gray-600 hover:text-green-600"
              }`}
            >
              ✓ Bestätigt ({stats.confirmed})
            </button>
            <button
              onClick={() => setStatusFilter("rejected")}
              className={`flex-1 py-4 px-4 md:px-6 font-semibold transition text-sm md:text-base ${
                statusFilter === "rejected"
                  ? "border-b-2 border-red-600 text-red-600"
                  : "text-gray-600 hover:text-red-600"
              }`}
            >
              ✗ Storniert ({reservations.filter(r => r.status === 'rejected').length})
            </button>
          </div>
        </div>

        {/* Reservations List */}
        <div className="space-y-4">
          {filteredReservations.length === 0 ? (
            <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
              <Calendar className="w-16 h-16 mx-auto mb-4 text-gray-400" />
              <p className="text-gray-600 text-lg">Keine Reservierungen gefunden</p>
            </div>
          ) : (
            filteredReservations.map((reservation) => (
              <div 
                key={reservation.id} 
                id={`reservation-${reservation.id}`}
                data-testid={`admin-reservation-${reservation.id}`}
                className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition"
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <h3 className="text-xl font-bold" style={{color: '#2c2c2c'}}>
                        {reservation.first_name} {reservation.last_name}
                      </h3>
                      {getStatusBadge(reservation.status)}
                    </div>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-gray-600">Datum</p>
                        <p className="font-semibold">{format(parse(reservation.reservation_date, "yyyy-MM-dd", new Date()), "dd.MM.yyyy", { locale: de })}</p>
                      </div>
                      <div>
                        <p className="text-gray-600">Uhrzeit</p>
                        <p className="font-semibold">{reservation.reservation_time} Uhr</p>
                      </div>
                      <div className="min-w-0">
                        <GuestSummary reservation={reservation} prefix={`admin-${reservation.id}`} />
                      </div>
                      <div>
                        <p className="text-gray-600">Telefon</p>
                        <p className="font-semibold">{reservation.phone}</p>
                      </div>
                      <div className="col-span-2">
                        <p className="text-gray-600">E-Mail</p>
                        <p className="font-semibold">{reservation.email}</p>
                      </div>
                      {reservation.special_requests && (
                        <div className="col-span-2">
                          <p className="text-gray-600">Spezielle Wünsche</p>
                          <p className="font-semibold">{reservation.special_requests}</p>
                        </div>
                      )}
                      {reservation.attachment_filename && (
                        <div className="col-span-2">
                          <p className="text-gray-600">Anhang</p>
                          <div className="flex items-center gap-2">
                            <p className="font-semibold text-blue-600">📎 {reservation.attachment_filename}</p>
                            {reservation.attachment_data && (
                              <a
                                href={`data:${reservation.attachment_mimetype};base64,${reservation.attachment_data}`}
                                download={reservation.attachment_filename}
                                className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded hover:bg-blue-200"
                              >
                                Herunterladen
                              </a>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col gap-2 ml-4">
                    {/* Status Dropdown */}
                    <select
                      data-testid={`admin-status-${reservation.id}`}
                      value={reservation.status}
                      onChange={(e) => handleStatusChange(reservation.id, e.target.value)}
                      className="px-3 py-2 border rounded-md text-sm font-semibold"
                      style={{
                        borderColor: reservation.status === 'confirmed' ? '#10b981' : 
                                   reservation.status === 'pending' ? '#f59e0b' :
                                   reservation.status === 'completed' ? '#6b7280' : '#ef4444'
                      }}
                    >
                      <option value="pending">⚠️ Anfrage</option>
                      <option value="confirmed">✓ Bestätigt</option>
                      <option value="completed">📦 Erledigt</option>
                      <option value="rejected">✗ Abgelehnt</option>
                    </select>
                    
                    <Button
                      data-testid={`admin-print-${reservation.id}`}
                      onClick={() => printReservation(reservation)}
                      variant="outline"
                      size="sm"
                      className="bg-blue-50 text-blue-700 hover:bg-blue-100 border-blue-300"
                      title="Reservierung drucken"
                    >
                      <Printer className="w-4 h-4 mr-1" />
                      Drucken
                    </Button>
                    <Button
                      data-testid={`admin-edit-${reservation.id}`}
                      onClick={() => openEditDialog(reservation)}
                      variant="outline"
                      size="sm"
                    >
                      <Edit className="w-4 h-4 mr-1" />
                      Bearbeiten
                    </Button>
                    {reservation.status !== "completed" && reservation.status !== "rejected" && (
                      <Button
                        onClick={() => handleMarkAsCompleted(reservation.id)}
                        variant="outline"
                        size="sm"
                        className="text-gray-600"
                      >
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Erledigt
                      </Button>
                    )}
                    {reservation.status === "pending" && (
                      <>
                        <Button
                          onClick={() => handleConfirmReservation(reservation.id)}
                          size="sm"
                          className="bg-green-600 text-white hover:bg-green-700"
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Bestätigen
                        </Button>
                        <Button
                          onClick={() => openRejectDialog(reservation)}
                          size="sm"
                          className="bg-orange-600 text-white hover:bg-orange-700"
                        >
                          <XCircle className="w-4 h-4 mr-1" />
                          Ablehnen
                        </Button>
                      </>
                    )}
                    {reservation.status !== "cancelled" && (
                      <Button
                        onClick={() => handleStatusChange(reservation.id, "cancelled")}
                        variant="outline"
                        size="sm"
                        className="text-red-600"
                      >
                        <XCircle className="w-4 h-4 mr-1" />
                        Stornieren
                      </Button>
                    )}
                    <Button
                      onClick={() => handleDeleteReservation(reservation.id)}
                      variant="outline"
                      size="sm"
                      className="text-red-600 border-red-300 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Löschen
                    </Button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Edit Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent data-testid="admin-edit-dialog" aria-describedby={undefined} className="max-w-xl w-[95vw] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Reservierung bearbeiten</DialogTitle>
            </DialogHeader>
            {editingReservation && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Vorname</Label>
                    <Input
                      data-testid="admin-edit-first-name"
                      value={editFirstName}
                      onChange={(e) => setEditFirstName(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Nachname</Label>
                    <Input
                      data-testid="admin-edit-last-name"
                      value={editLastName}
                      onChange={(e) => setEditLastName(e.target.value)}
                    />
                  </div>
                </div>
                <div>
                  <Label>Datum</Label>
                  <Button
                    data-testid="admin-edit-date-button"
                    onClick={() => setShowEditCalendar(!showEditCalendar)}
                    variant="outline"
                    className="w-full justify-start"
                  >
                    <Calendar className="w-4 h-4 mr-2" />
                    {format(editDate, "dd.MM.yyyy")}
                  </Button>
                  {showEditCalendar && (
                    <div className="mt-2">
                      <CalendarComponent
                        mode="single"
                        selected={editDate}
                        onSelect={(date) => {
                          setEditDate(date);
                          setShowEditCalendar(false);
                        }}
                        locale={de}
                      />
                    </div>
                  )}
                </div>
                <div>
                  <Label>Uhrzeit</Label>
                  <Input
                    data-testid="admin-edit-time-input"
                    type="time"
                    value={editTime}
                    onChange={(e) => setEditTime(e.target.value)}
                  />
                </div>
                <ReservationGuestEditor reservation={editingReservation} value={editGuestCounts}
                  onChange={setEditGuestCounts} prefix="admin-edit" disabled={savingEdit} />
                <div>
                  <Label>Spezielle Wünsche</Label>
                  <Textarea
                    data-testid="admin-edit-special-requests"
                    value={editSpecialRequests}
                    onChange={(e) => setEditSpecialRequests(e.target.value)}
                  />
                </div>
                <Button data-testid="admin-edit-save-button" onClick={handleSaveEdit}
                  disabled={savingEdit || (editGuestCounts !== null && !validGuestCounts(editGuestCounts))}
                  className="w-full" style={{backgroundColor: '#c8232c'}}>
                  {savingEdit ? "Speichert..." : "Änderungen speichern"}
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Blocked Slots Section */}
        {blockedSlots.length > 0 && (
          <div className="bg-white rounded-2xl shadow-lg p-6 mt-6">
            <h2 className="text-xl font-bold mb-4" style={{color: '#2c2c2c'}}>
              <Ban className="inline-block w-6 h-6 mr-2" />
              Gesperrte Zeit-Slots
            </h2>
            <div className="space-y-2">
              {blockedSlots.map((slot) => (
                <div key={slot.id} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-semibold">
                      {format(parse(slot.date, "yyyy-MM-dd", new Date()), "dd.MM.yyyy", { locale: de })}
                    </p>
                    <p className="text-sm text-gray-600">
                      {slot.is_full_day
                        ? "Ganzer Tag gesperrt"
                        : `${slot.time_from} - ${slot.time_to} Uhr`}
                    </p>
                    {slot.reason && (
                      <p className="text-sm text-gray-500 italic">{slot.reason}</p>
                    )}
                  </div>
                  <Button
                    onClick={() => handleUnblockTimeSlot(slot.id)}
                    variant="outline"
                    size="sm"
                  >
                    Entsperren
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Reject Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Anfrage ablehnen</DialogTitle>
          </DialogHeader>
          {rejectingReservation && (
            <div className="space-y-4">
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600">Anfrage von:</p>
                <p className="font-semibold">{rejectingReservation.first_name} {rejectingReservation.last_name}</p>
                <p className="text-sm text-gray-600 mt-2">
                  {rejectingReservation.guests_count} Personen am {format(parse(rejectingReservation.reservation_date, "yyyy-MM-dd", new Date()), "dd.MM.yyyy", { locale: de })} um {rejectingReservation.reservation_time} Uhr
                </p>
                <GuestSummary reservation={rejectingReservation} prefix="admin-reject" />
              </div>
              
              <div>
                <Label className="mb-2 block">Vordefinierte Gründe</Label>
                <div className="grid grid-cols-1 gap-2">
                  {predefinedReasons.map((reason, index) => (
                    <button
                      key={index}
                      onClick={() => setRejectionReason(reason)}
                      className={`p-2 text-left text-sm border rounded transition ${
                        rejectionReason === reason
                          ? 'border-red-500 bg-red-50'
                          : 'border-gray-300 hover:border-red-300'
                      }`}
                    >
                      {reason}
                    </button>
                  ))}
                </div>
              </div>
              
              <div>
                <Label>Eigene Begründung / Details</Label>
                <Textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Geben Sie hier eine Begründung ein..."
                  rows={4}
                  className="mt-2"
                />
              </div>
              
              <div className="flex gap-2">
                <Button
                  onClick={handleRejectReservation}
                  disabled={!rejectionReason.trim()}
                  className="flex-1 bg-orange-600 hover:bg-orange-700 text-white"
                >
                  Ablehnen & E-Mail senden
                </Button>
                <Button
                  onClick={() => {
                    setShowRejectDialog(false);
                    setRejectionReason("");
                  }}
                  variant="outline"
                  className="flex-1"
                >
                  Abbrechen
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Print Template - Hidden on screen, shown only when printing */}
      {printingReservation && (
        <div className="print-only" style={{ display: 'none' }}>
          {printerFormat === 'receipt' ? (
            <ReceiptPrintTemplate reservation={printingReservation} />
          ) : (
            <A4PrintTemplate reservation={printingReservation} />
          )}
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
